import { useSyncExternalStore } from 'react';

// Identidad visual de la instalación: la define el .env del servidor
// (BRAND_*) y llega por /api/marca. Nada de la marca de una organización
// concreta vive en el código.

export type Marca = {
  producto: string;
  nombre: string;
  titulo: string;
  lema: string;
  color: string;
  colorOscuro: string | null;
  acento: string;
  logo: string | null;
  logoOscuro: string | null;
  favicon: string | null;
};

const GENERICA: Marca = {
  producto: 'Cloud',
  nombre: '',
  titulo: 'Cloud',
  lema: 'Almacenamiento de archivos',
  color: '#1F5FAD',
  colorOscuro: null,
  acento: '#1A9E8F',
  logo: null,
  logoOscuro: null,
  favicon: null,
};

const CLAVE_LOCAL = 'cloud.marca';
const HEX = /^#[0-9a-fA-F]{6}$/;

let actual: Marca = leerLocal() ?? GENERICA;
const oyentes = new Set<() => void>();

/**
 * Se espeja en localStorage para pintar la marca correcta antes de que
 * responda /api/marca, igual que el tema.
 */
function leerLocal(): Marca | null {
  try {
    const crudo = localStorage.getItem(CLAVE_LOCAL);
    if (!crudo) return null;
    const m = JSON.parse(crudo) as Marca;
    return typeof m.producto === 'string' && HEX.test(m.color) ? m : null;
  } catch {
    return null;
  }
}

export function aplicarMarca(m: Marca = actual): void {
  actual = m;
  const root = document.documentElement.style;
  root.setProperty('--marca-primario', m.color);
  // Sin variante oscura explícita, se aclara el color para que se lea sobre
  // fondo oscuro.
  root.setProperty('--marca-primario-oscuro', m.colorOscuro ?? `color-mix(in srgb, ${m.color} 55%, white)`);
  root.setProperty('--marca-acento', m.acento);

  if (!document.title.includes(' · ')) document.title = m.titulo;

  let icono = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (m.favicon) {
    if (!icono) {
      icono = document.createElement('link');
      icono.rel = 'icon';
      document.head.appendChild(icono);
    }
    icono.href = m.favicon;
  } else {
    icono?.remove();
  }

  try {
    localStorage.setItem(CLAVE_LOCAL, JSON.stringify(m));
  } catch {
    // Modo privado: se vuelve a pedir en la próxima carga.
  }
  oyentes.forEach((f) => f());
}

export async function cargarMarca(): Promise<void> {
  try {
    const r = await fetch('/api/marca', { credentials: 'same-origin' });
    if (r.ok) aplicarMarca((await r.json()) as Marca);
  } catch {
    // Sin respuesta queda la última conocida, o la genérica.
  }
}

export function useMarca(): Marca {
  return useSyncExternalStore(
    (f) => {
      oyentes.add(f);
      return () => oyentes.delete(f);
    },
    () => actual,
  );
}

/**
 * Logo de la instalación. Con versión para modo oscuro se alternan por CSS;
 * sin ella, el mismo logo se aclara en modo oscuro. Sin logo, una nube genérica.
 */
export function Logo({ className }: { className: string }) {
  const marca = useMarca();
  const alt = marca.nombre || marca.producto;

  if (!marca.logo) {
    return (
      <svg className={`${className} logo-generico`} viewBox="0 0 48 32" role="img" aria-label={alt}>
        <path d="M38.5 13.1A12 12 0 0 0 15.3 9.6 9 9 0 0 0 11 27.5h27a7.3 7.3 0 0 0 .5-14.4Z" />
      </svg>
    );
  }

  if (!marca.logoOscuro) {
    return <img src={marca.logo} alt={alt} className={`${className} logo-invertible`} />;
  }

  return (
    <>
      <img src={marca.logo} alt={alt} className={`${className} logo-claro`} />
      <img src={marca.logoOscuro} alt={alt} className={`${className} logo-oscuro`} />
    </>
  );
}
